import axios from 'axios'

const state = {
  taggingMode: 'photo',  // 'photo' or 'group'
  captureMode: 'tag',    // 'tag' or 'count'
  tagVerificationEnabled: false,
  verificationTargetPerStratum: 30,
  verificationFloorPerStratum: 5,
  // IT-703: per-tenant options for the group-tagging Product Category filter.
  // Empty for every tenant that has no question-level split, which hides the
  // dropdown rather than showing another platform's categories.
  productCategoryOptions: [],
  // IT-704 (P4): when true, the tagger/QC surfaces hide machine-owned
  // (hierarchical) categories and omit their derived tags from save payloads,
  // because the backend recomputes them. Driven by the tenant setting of the
  // same name — only enabled once P3 write-path derivation is deployed.
  hierarchicalTaggingEnabled: false,
  // When true, the group-tagging page shows each photo's notes (the FA question's
  // custom_data) read-only under the photo ID. Per-tenant; MVP is Family Dollar.
  photoNotesDisplayEnabled: false,
  settingsLoaded: false,
  settingsPromise: null
}

const getters = {
  isGroupMode: (state) => state.taggingMode === 'group',
  isPhotoMode: (state) => state.taggingMode === 'photo',
  isCountMode: (state) => state.captureMode === 'count',
  isBboxMode: (state) => state.captureMode === 'bbox',
  isTagMode: (state) => state.captureMode === 'tag',
  isTagVerificationEnabled: (state) => state.tagVerificationEnabled === true,
  isHierarchicalTaggingEnabled: (state) => state.hierarchicalTaggingEnabled === true,
  isPhotoNotesDisplayEnabled: (state) => state.photoNotesDisplayEnabled === true,
  taggerHomeRoute: (state) => {
    const isCount = state.captureMode === 'count'
    const isGroup = state.taggingMode === 'group'
    // Bbox mode is group-only in practice — share of shelf is measured across a
    // whole shelf section — but fall back gracefully if a tenant sets photo mode.
    if (state.captureMode === 'bbox') return '/box-tagging'
    if (isCount) return isGroup ? '/group-counting' : '/counting'
    return isGroup ? '/group-tagging' : '/tagging'
  },
  qcHomeRoute: (state) => {
    const isCount = state.captureMode === 'count'
    const isGroup = state.taggingMode === 'group'
    if (state.captureMode === 'bbox') return '/box-tagging'
    if (isCount) return isGroup ? '/group-count-qc' : '/count-qc'
    return isGroup ? '/group-qc' : '/quality-control'
  },
  adminHomeRoute: (state) => state.captureMode === 'count' ? '/manage-count-items' : '/manage-tags'
}

const mutations = {
  setTaggingMode(state, mode) {
    state.taggingMode = mode
  },
  setCaptureMode(state, mode) {
    state.captureMode = mode
  },
  setTagVerificationEnabled(state, enabled) {
    state.tagVerificationEnabled = !!enabled
  },
  setVerificationTargetPerStratum(state, n) {
    if (n != null) state.verificationTargetPerStratum = n
  },
  setVerificationFloorPerStratum(state, n) {
    if (n != null) state.verificationFloorPerStratum = n
  },
  setProductCategoryOptions(state, options) {
    state.productCategoryOptions = options
  },
  setHierarchicalTaggingEnabled(state, enabled) {
    state.hierarchicalTaggingEnabled = !!enabled
  },
  setPhotoNotesDisplayEnabled(state, enabled) {
    state.photoNotesDisplayEnabled = !!enabled
  },
  setSettingsLoaded(state, loaded) {
    state.settingsLoaded = loaded
  },
  setSettingsPromise(state, promise) {
    state.settingsPromise = promise
  }
}

const actions = {
  fetchSettings(context) {
    // Cache the in-flight promise so concurrent callers (router guard + App mount + Login)
    // share the same request instead of racing.
    if (context.state.settingsPromise) {
      return context.state.settingsPromise
    }
    const promise = axios.get('/api/settings/')
      .then(response => {
        context.commit('setTaggingMode', response.data.tagging_mode)
        context.commit('setCaptureMode', response.data.capture_mode || 'tag')
        context.commit('setTagVerificationEnabled', response.data.tag_verification_enabled)
        context.commit('setVerificationTargetPerStratum', response.data.verification_target_per_stratum)
        context.commit('setVerificationFloorPerStratum', response.data.verification_floor_per_stratum)
        context.commit('setProductCategoryOptions', response.data.product_category_options || [])
        context.commit('setHierarchicalTaggingEnabled', response.data.hierarchical_tagging_enabled)
        context.commit('setPhotoNotesDisplayEnabled', response.data.photo_notes_display_enabled)
        context.commit('setSettingsLoaded', true)
      })
      .catch(e => {
        console.log(e)
        context.commit('setSettingsPromise', null)
      })
    context.commit('setSettingsPromise', promise)
    return promise
  }
}

export default {
  state,
  getters,
  mutations,
  actions
}
